import { useState } from "react";
import { X } from "lucide-react";
import { createSubject } from "@/lib/api";
import { SUBJECT_COLORS, SUBJECT_ICONS } from "@/lib/subjectMeta";
import {
  BookOpen, Atom, Calculator, FlaskConical, Globe,
  Languages, Music, Palette, Cpu, Leaf, Brain, Heart,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { BookOpen, Atom, Calculator, FlaskConical, Globe, Languages, Music, Palette, Cpu, Leaf, Brain, Heart };

export default function CreateSubjectDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("primary");
  const [icon, setIcon] = useState("BookOpen");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const s = await createSubject({ name: name.trim(), color, icon });
      onCreated?.(s);
      setName("");
      onClose();
      toast.success("Subject created");
    } catch (err) {
      toast.error("Failed to create subject");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="nb-border bg-white dark:bg-zinc-900 rounded-2xl p-6 w-full max-w-md nb-shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">New Subject</h2>
          <button type="button" onClick={onClose} className="nb-button p-2" aria-label="close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="label-caps block mb-2">Name</label>
        <input
          data-testid="new-subject-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Biology, Algebra II"
          className="nb-input mb-4"
        />

        <label className="label-caps block mb-2">Color</label>
        <div className="flex gap-2 mb-4 flex-wrap">
          {Object.entries(SUBJECT_COLORS).map(([key, c]) => (
            <button
              type="button"
              key={key}
              data-testid={`color-${key}`}
              onClick={() => setColor(key)}
              className={`w-10 h-10 rounded-xl nb-border ${c.chip} transition-all ${color === key ? "nb-shadow scale-110" : ""}`}
              aria-label={key}
            />
          ))}
        </div>

        <label className="label-caps block mb-2">Icon</label>
        <div className="grid grid-cols-6 gap-2 mb-6">
          {SUBJECT_ICONS.map((key) => {
            const Ico = ICONS[key];
            return (
              <button
                type="button"
                key={key}
                data-testid={`icon-${key}`}
                onClick={() => setIcon(key)}
                className={`nb-border rounded-xl p-2 flex items-center justify-center bg-white dark:bg-zinc-900 ${
                  icon === key ? "nb-shadow bg-[#FFE066] dark:bg-[#F6E05E]" : ""
                }`}
              >
                <Ico className="w-5 h-5" strokeWidth={2.5} />
              </button>
            );
          })}
        </div>

        <button
          data-testid="create-subject-submit"
          type="submit"
          disabled={saving || !name.trim()}
          className="nb-button w-full py-3 bg-[#D4C4FB] dark:bg-[#B794F4]"
        >
          {saving ? "Creating…" : "Create Subject"}
        </button>
      </form>
    </div>
  );
}
